import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import Grid from '../../../components/grid'
import { useQuery } from '@apollo/react-hooks'
import { Query } from '../../../api'
import Loading from '../../../components/Loading'
import { useTranslation, Trans } from 'react-i18next'
import { Download, Eye, FileText, Users } from 'lucide-react'
import { FilePreviewModal, isPreviewable, getPreviewFileType } from '@/components/file-preview'
import type { PreviewFile } from '@/components/file-preview'
import FileIcon from '../../../components/file-manager/FileIcon'
import i18next from 'i18next'
import { stringToColor } from '../../../utils/functions'
import { API_URL } from '../../../utils'
import { formatBytes } from '../../../components/grid/formatters'
import { useModuleActions } from '../../../hooks/useMyPermissions'

const BidderSelectInput = ({ selection, onChange }) => {
  const activeLanguage = i18next.language.toUpperCase()

  const { t } = useTranslation()
  const { data } = useQuery(Query.BIDDERS)
  const [selected, setSelected] = useState([])
  const [open, setOpen] = useState(false)
  const rows = (data ? data.bidders : []).map((b) => ({
    ...b,
    tags: b.tags?.[activeLanguage],
  }))

  const [filteredRows, setFilteredRows] = useState(null)

  useEffect(() => {
    setSelected(selection)
  }, [selection, open])

  const handleClose = (s?: any) => {
    setOpen(false)
    onChange(s ?? selected)
  }
  const customFilter = (val: string) => {
    const containsFieldsFilter = ['contactName', 'email', 'name', 'phone']

    const newRows = rows.filter((row) => {
      for (let i = 0; i < containsFieldsFilter.length; i++) {
        if (row[containsFieldsFilter[i]].toLowerCase().includes(val.toLowerCase())) return true
      }

      if (row.tags?.some((tag) => tag.toLowerCase().includes(val.toLowerCase()))) return true

      return false
    })
    setFilteredRows(newRows)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col overflow-y-hidden">
          <DialogHeader>
            <DialogTitle>
              {t('Procurement_tenders_tender_detail_bidders_selection_modal_heading')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 flex flex-col [&_td:first-child]:!pl-3 [&_th:first-child]:!pl-3">
            <Grid
              customFilter={customFilter}
              actionBar
              scrollable
              selection={selected}
              selectByRowClick
              onSelectionChange={setSelected}
              rows={(filteredRows ?? rows).map((row) => ({
                ...row,
                tags: (
                  <div className="flex flex-wrap gap-1">
                    {row?.tags?.map((tag: string, i: number) => {
                      const color = stringToColor(tag)
                      return (
                        <Badge
                          key={i}
                          variant="outline"
                          className="text-xs font-medium"
                          style={{ borderColor: color, color, backgroundColor: `${color}10` }}
                        >
                          {tag}
                        </Badge>
                      )
                    })}
                  </div>
                ),
              }))}
              columns={[
                {
                  name: 'name',
                  title: t('Procurement_tenders_tender_detail_bidders_list_name_label'),
                },
                {
                  name: 'contactName',
                  title: t('Procurement_tenders_tender_detail_bidders_list_contact_name_label'),
                },
                {
                  name: 'email',
                  title: t('Procurement_tenders_tender_detail_bidders_list_email_label'),
                },
                {
                  name: 'phone',
                  title: t('Procurement_bidders_phone_label'),
                },
                {
                  name: 'tags',
                  title: t('TAGS'),
                },
              ]}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleClose()}>
              {t('Procurement_tenders_tender_detail_modal_cancel_btn')}
            </Button>
            <Button onClick={() => handleClose(selected)}>
              <Trans
                i18nKey="Procurement_tenders_tender_detail_modal_select_x_bidders_btn"
                count={selected.length}
              >
                Select {{ count: selected.length }} bidders
              </Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Users className="h-4 w-4" />
        {t('Procurement_tenders_tender_detail_bidders_list_manage_bidders_btn')}
      </Button>
    </>
  )
}

export default ({
  onSelectionChange,
  onResendNotification,
  onCreateBid,
  selection,
  readOnly,
  tenderStatus,
  winnerId,
  showCreateBid = true,
  tender,
  round,
  projectId,
}) => {
  const activeLanguage = i18next.language.toUpperCase()

  const { t } = useTranslation()
  const { canAdmin, canEdit } = useModuleActions('PROCUREMENT', projectId)
  const { data, loading } = useQuery(Query.BIDDERS)
  const [previewFiles, setPreviewFiles] = useState<PreviewFile[]>([])
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  const rows = data
    ? data.bidders
        .filter((b) => selection.indexOf(b.id) > -1)
        .map((b) => {
          const bid = tender?.bids?.find((bid: any) => bid.bidder?.id === b.id && (!round || bid.round === round))
          return { ...b, _files: bid?.files || [] }
        })
    : []
  const [filteredRows, setFilteredRows] = useState(null)

  const customFilter = (val: string) => {
    const containsFieldsFilter = ['contactName', 'email', 'name']

    const newRows = rows.filter((row) => {
      for (let i = 0; i < containsFieldsFilter.length; i++) {
        if (row[containsFieldsFilter[i]].toLowerCase().includes(val.toLowerCase())) return true
      }

      if (row.tags?.[activeLanguage]?.some((tag) => tag.toLowerCase().includes(val.toLowerCase())))
        return true

      return false
    })
    setFilteredRows(newRows)
  }

  return (
    <>
      <Grid
        customFilter={customFilter}
        actionBar={{
          actionButton: readOnly ? null : (
            <BidderSelectInput selection={selection} onChange={onSelectionChange} />
          ),
        }}
        rows={filteredRows ?? rows}
        columns={[
          { name: 'name', title: t('Procurement_tenders_tender_detail_bidders_list_name_label') },
          {
            name: 'contactName',
            title: t('Procurement_tenders_tender_detail_bidders_list_contact_name_label'),
          },
          { name: 'email', title: t('Procurement_tenders_tender_detail_bidders_list_email_label') },
          { name: '_files', title: t('Tender_bids_table_column_files') },
        ]}
        formatters={[
          {
            formatter: ({ value }) => {
              const files = value || []
              if (files.length === 0) return <span className="text-muted-foreground">—</span>
              if (tenderStatus === 'RUNNING') {
                return (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" />
                    <span>{files.length}</span>
                  </span>
                )
              }
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{files.length}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 p-0">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium">{t('Tender_bids_table_column_files')} ({files.length})</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto divide-y">
                      {files.map((file: any) => {
                        const canPreview = file.mimetype && isPreviewable(file.mimetype)
                        const handlePreview = () => {
                          const previews: PreviewFile[] = files
                            .filter((f: any) => f.mimetype && isPreviewable(f.mimetype))
                            .map((f: any) => ({
                              url: `${API_URL}/download/${f.id}`,
                              title: f.filename,
                              downloadUrl: `${API_URL}/download/${f.id}`,
                              type: getPreviewFileType(f.mimetype)!,
                              fileId: f.id,
                              fileSize: f.size,
                            }))
                          const idx = previews.findIndex((p) => p.fileId === file.id)
                          setPreviewFiles(previews)
                          setPreviewIndex(idx >= 0 ? idx : 0)
                        }

                        return canPreview ? (
                          <button
                            key={file.id}
                            onClick={handlePreview}
                            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors w-full text-left"
                          >
                            <FileIcon filename={file.filename} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate">{file.filename}</p>
                              <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
                            </div>
                            <Eye className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        ) : (
                          <a
                            key={file.id}
                            href={`${API_URL}/download/${file.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                          >
                            <FileIcon filename={file.filename} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate">{file.filename}</p>
                              <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
                            </div>
                            <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </a>
                        )
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )
            },
            for: ['_files'],
          },
        ]}
        noDataMessage={t(
          'Procurement_tenders_tender_detail_documents_no_bidders_selected_placeholder',
        )}
        rowActions={
          (() => {
            const actions = [
              showCreateBid && canAdmin && {
                title: ({ id }) => {
                  const existingBid = tender?.bids?.find(
                    (bid: any) => bid.bidder?.id === id
                  )
                  return existingBid
                    ? t('Tender_form_update_bid_button')
                    : t('Tender_form_create_bid_button')
                },
                color: 'primary',
                action: ({ id }) => onCreateBid(id),
              },
              !readOnly && {
                title: t('Procurement_tenders_tender_detail_documents_remove_btn'),
                color: 'danger',
                action: ({ id }) => {
                  onSelectionChange(selection.filter((b) => b !== id))
                },
              },
              !readOnly && tenderStatus === 'RUNNING' && canEdit && {
                title: t('Procurement_tenders_tender_detail_documents_resend_btn'),
                action: ({ id }) => onResendNotification(id),
              },
            ].filter(Boolean)

            return actions.length > 0 ? actions : null
          })()
        }
      />
      {loading && <Loading />}
      {previewFiles.length > 0 && previewIndex !== null && (
        <FilePreviewModal
          isOpen={true}
          files={previewFiles}
          currentIndex={previewIndex}
          onNavigate={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </>
  )
}
